<template>
	<section class="institution-demo">
		<InstitutionSearchPanel
			:categories="categories"
			:active-category-id="activeCategoryId"
			:selected-region-code="selectedRegionCode"
			:regions="regions"
			:regions-loading="regionsLoading"
			:institutions="institutions"
			:selected-merchant-id="selectedMerchantId"
			:loading="loading"
			:placeholder="t(`${I18N_NS}.search.placeholder`)"
			:search-label="t(`${I18N_NS}.search.searchBtn`)"
			:result-count-text="resultCountText"
			:list-title="listTitle"
			@category-change="handleCategoryChange"
			@region-select="handleRegionSelect"
			@select-institution="handleSelectInstitution"
			@search="handleSearch"
		/>

		<div v-if="detailViewOpen" class="demo-detail-overlay">
			<div class="demo-detail-hero">
				<swiper
					v-if="heroPhotos.length > 1 && swiperReady"
					:key="heroCarouselKey"
					class="demo-detail-hero-swiper"
					:modules="swiperModules"
					:loop="heroPhotos.length > 1"
					:autoplay="{
						delay: 4000,
						disableOnInteraction: false,
					}"
					:pagination="{ clickable: true }"
				>
					<swiper-slide v-for="(photo, index) in heroPhotos" :key="`${photo}-${index}`">
						<img
							class="demo-detail-hero-image"
							:src="photo"
							:alt="`${detail?.title || ''} ${index + 1}`"
						/>
					</swiper-slide>
				</swiper>
				<img
					v-else-if="heroPhotos[0]"
					class="demo-detail-hero-image"
					:src="heroPhotos[0]"
					:alt="detail?.title || ''"
				/>
				<button
					type="button"
					class="demo-detail-back"
					:aria-label="t(`${I18N_NS}.detail.backAria`)"
					@click="closeDetail"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="24"
						height="40"
						viewBox="0 0 12 20"
						fill="none"
						aria-hidden="true"
					>
						<path
							d="M10.5 1.5L2 10L10.5 18.5"
							stroke="#FFFFFF"
							stroke-width="2"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</svg>
				</button>
			</div>

			<div v-if="detailLoading" class="demo-detail-loading" aria-hidden="true">
				<div class="demo-detail-skeleton">
					<span class="demo-detail-skeleton__title"></span>
					<span class="demo-detail-skeleton__meta"></span>
					<div class="demo-detail-skeleton__card"></div>
					<span v-for="line in 5" :key="line" class="demo-detail-skeleton__line"></span>
				</div>
			</div>
			<div v-else-if="detail" class="demo-detail-sheet">
				<InstitutionDetailPanel v-bind="detail" />
			</div>
		</div>
	</section>
</template>

<script setup>
import { computed, defineAsyncComponent, onMounted, ref, watch } from "vue";
import InstitutionSearchPanel from "./search-panel.vue";
import { useI18n } from "~/composables/useI18n";
import { localeToApiLang } from "~/utils/request-lang";
import { getMerchantTypes } from "~/api/api";
import {
	useServiceInstitutionCache,
	useServiceInstitutionData,
} from "../../../pc/composables/useServiceInstitutionData";
import {
	getInstitutionParams,
	useServiceInstitutionTracking,
} from "../../../pc/composables/useServiceInstitutionTracking";
import catAll from "../../../pc/image/category/all.png";
import catNearby from "../../../pc/image/category/nearby.png";

const I18N_NS = "serviceInstitution.pc.demo";
const InstitutionDetailPanel = defineAsyncComponent(() => import("./detail-panel.vue"));
const Swiper = defineAsyncComponent(() => import("swiper/vue").then(module => module.Swiper));
const SwiperSlide = defineAsyncComponent(() =>
	import("swiper/vue").then(module => module.SwiperSlide)
);

const { t, locale } = useI18n();
const { track } = useServiceInstitutionTracking("mobile");
const detailViewOpen = ref(false);
const detailSourceAction = ref("default");
const swiperModules = ref([]);
const swiperReady = computed(() => swiperModules.value.length > 0);
const pageCache = useServiceInstitutionCache();

const getLangRequestConfig = lang => ({
	headers: {
		Lang: localeToApiLang(lang || locale.value),
	},
});

const fetchMerchantTypes = async lang => {
	const response = await getMerchantTypes(getLangRequestConfig(lang));
	if (response?.code !== 200 || !Array.isArray(response?.data)) {
		return [];
	}
	return response.data;
};

const { data: merchantTypes } = useAsyncData(
	() => `service-institution-merchant-types-m-${locale.value}`,
	async () => {
		const cacheKey = String(locale.value);
		if (Object.prototype.hasOwnProperty.call(pageCache.value.merchantTypes, cacheKey)) {
			return pageCache.value.merchantTypes[cacheKey];
		}

		const data = await fetchMerchantTypes(locale.value);
		pageCache.value.merchantTypes[cacheKey] = data;
		return data;
	},
	{
		default: () => pageCache.value.merchantTypes[String(locale.value)] || [],
		lazy: true,
		server: false,
		watch: [locale],
	}
);

const fixedCategories = computed(() => [
	{ id: "nearby", label: t(`${I18N_NS}.categories.nearby`), icon: catNearby },
	{ id: "all", label: t(`${I18N_NS}.categories.all`), icon: catAll },
]);

const categories = computed(() => {
	const apiCategories = (merchantTypes.value || [])
		.map(item => ({
			id: item.id,
			label: item.name,
			icon: item.small_icon_url || item.icon_url,
			color: item.color,
		}))
		.filter(item => item.id && item.label && item.icon);

	return [...fixedCategories.value, ...apiCategories];
});

const {
	activeCategoryId,
	selectedMerchantId,
	selectedRegionCode,
	selectedRegion,
	regions,
	regionsLoading,
	loading,
	detailLoading,
	institutions,
	detail,
	resultCountText,
	searchKeyword,
	setActiveCategory,
	selectRegion,
	selectMerchant,
	searchMerchants,
} = useServiceInstitutionData({ t, merchantTypes, locale });

const listTitle = computed(() => {
	if (searchKeyword.value.trim()) {
		return t(`${I18N_NS}.search.resultTitle`);
	}
	if (activeCategoryId.value === "nearby" && selectedRegion.value?.name) {
		return selectedRegion.value.name;
	}
	const category = getCategory(activeCategoryId.value);
	return category?.label || t(`${I18N_NS}.categories.all`);
});

const heroPhotos = computed(() => detail.value?.photos?.filter(Boolean) || []);
const heroCarouselKey = computed(() => `${detail.value?.id || ""}-${heroPhotos.value.join("|")}`);

function getCategory(categoryId) {
	return categories.value.find(item => String(item.id) === String(categoryId));
}

function getInstitution(merchantId) {
	return institutions.value.find(item => String(item.id) === String(merchantId));
}

async function handleCategoryChange(categoryId) {
	const previousCategory = getCategory(activeCategoryId.value);
	await setActiveCategory(categoryId);
	const category = getCategory(categoryId);

	track("service_category_filter_click", {
		category_id: categoryId,
		category_name: category?.label,
		previous_category: previousCategory?.label,
		result_count: institutions.value.length,
	});
}

async function handleRegionSelect(region) {
	await selectRegion(region);
	track("service_category_filter_click", {
		category_id: "nearby",
		category_name: region?.name,
		previous_category: t(`${I18N_NS}.categories.nearby`),
		result_count: institutions.value.length,
	});
}

async function handleSearch(keyword) {
	await searchMerchants(keyword);
	track("service_search_submit", {
		keyword,
		keyword_length: keyword.length,
		result_count: institutions.value.length,
		has_result: institutions.value.length > 0,
	});
}

function handleSelectInstitution(merchantId, sourceAction = "result_card") {
	const institution = getInstitution(merchantId);
	if (sourceAction === "result_card") {
		track(
			"institution_result_click",
			getInstitutionParams(institution, {
				position: institutions.value.findIndex(item => String(item.id) === String(merchantId)) + 1,
			})
		);
	}
	detailSourceAction.value = sourceAction;
	selectMerchant(merchantId);
	detailViewOpen.value = true;
	ensureSwiper();
}

function closeDetail() {
	detailViewOpen.value = false;
}

onMounted(() => {
	track("service_org_page_view");
});

async function ensureSwiper() {
	if (swiperModules.value.length) return;
	const [{ Pagination, Autoplay }] = await Promise.all([
		import("swiper"),
		import("swiper/css"),
		import("swiper/css/pagination"),
	]);
	swiperModules.value = [Pagination, Autoplay];
}

watch(
	() => detail.value?.id,
	id => {
		if (!id || !detail.value) return;
		track(
			"institution_detail_view",
			getInstitutionParams(detail.value, {
				source_action: detailSourceAction.value,
			})
		);
	},
	{ flush: "post" }
);
</script>

<style lang="scss" scoped>
.institution-demo {
	position: relative;
	padding: 32px 32px 48px;
	background: #fff;
}

.demo-detail-overlay {
	position: fixed;
	inset: 0;
	z-index: 500;
	display: block;
	background: #fff;
	overflow-x: hidden;
	overflow-y: auto;
	-webkit-overflow-scrolling: touch;
}

.demo-detail-hero {
	position: relative;
	flex-shrink: 0;
	height: 516px;
	overflow: hidden;
	background: #f6f6f6;
}

.demo-detail-hero-swiper {
	width: 100%;
	height: 100%;

	:deep(.swiper),
	:deep(.swiper-wrapper),
	:deep(.swiper-slide) {
		height: 100%;
	}

	:deep(.swiper-pagination) {
		bottom: 56px;
	}

	:deep(.swiper-pagination-bullet) {
		width: 12px;
		height: 12px;
		background: rgba(255, 255, 255, 0.65);
		opacity: 1;
	}

	:deep(.swiper-pagination-bullet-active) {
		width: 28px;
		border-radius: 12px;
		background: #fff;
	}
}

.demo-detail-hero-image {
	display: block;
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.demo-detail-back {
	position: absolute;
	top: 32px;
	left: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 64px;
	height: 64px;
	padding: 0;
	border: none;
	border-radius: 50%;
	background: rgba(0, 0, 0, 0.35);
	cursor: pointer;
	-webkit-tap-highlight-color: transparent;
	z-index: 3;

	svg {
		display: block;
		width: 24px;
		height: 40px;
	}
}

.demo-detail-sheet {
	position: relative;
	z-index: 2;
	min-height: calc(100dvh - 484px);
	margin-top: -32px;
	border-radius: 32px 32px 0 0;
	background: #fff;
	overflow: visible;
}

.demo-detail-loading {
	min-height: 480px;
	margin-top: -32px;
	padding: 44px 30px 70px;
	border-radius: 32px 32px 0 0;
	background: #fff;
	box-sizing: border-box;
}

.demo-detail-skeleton__title,
.demo-detail-skeleton__meta,
.demo-detail-skeleton__card,
.demo-detail-skeleton__line {
	display: block;
	background: linear-gradient(90deg, #eee 25%, #fafafa 42%, #eee 62%);
	background-size: 300% 100%;
	animation: institution-detail-m-shimmer 1.35s ease-in-out infinite;
}
.demo-detail-skeleton__title {
	width: 62%;
	height: 36px;
	border-radius: 7px;
}
.demo-detail-skeleton__meta {
	width: 38%;
	height: 22px;
	margin: 22px 0 30px;
	border-radius: 5px;
}
.demo-detail-skeleton__card {
	height: 180px;
	margin-bottom: 32px;
	border-radius: 16px;
}
.demo-detail-skeleton__line {
	height: 22px;
	margin: 18px 0;
	border-radius: 5px;
}
.demo-detail-skeleton__line:last-child {
	width: 58%;
}
@keyframes institution-detail-m-shimmer {
	to {
		background-position: -100% 0;
	}
}
</style>
